import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { showToast } from '../utils/toast';
import SEO from './SEO';
import UpgradeModal from './UpgradeModal';
import { PLANS, FREE_REVENUE_LIMIT, type PlanId } from '../config/plans';
import { fetchMyPlanStatus, money, type PlanStatus } from '../utils/creatorPlan';
import './PricingPage.css';

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<PlanStatus | null>(null);
  const [loggedIn, setLoggedIn] = useState(false);
  const [upgradeFor, setUpgradeFor] = useState<PlanId | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await supabase.auth.getUser();
        const isIn = !!data.user;
        setLoggedIn(isIn);
        setStatus(await fetchMyPlanStatus());
      } catch {
        setStatus(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const choosePlan = (planId: PlanId) => {
    if (planId === 'free') {
      navigate('/dashboard');
      return;
    }
    if (!loggedIn) {
      showToast.error('Please log in to upgrade');
      navigate('/login');
      return;
    }
    setUpgradeFor(planId);
  };

  const currentPlan = status?.known ? status.plan : 'free';

  return (
    <div className="pricing-page">
      <SEO
        title="Pricing - PokeShani"
        description="Start free with up to $250 of revenue. Upgrade to Pro for unlimited sales, still 0% platform fees and payment straight to your own PayPal."
        url="https://pokeshani.com/pricing"
        type="website"
      />

      <header className="pricing-header">
        <Link to="/" className="pricing-logo">
          PokeShani
        </Link>
        <Link to="/dashboard" className="pricing-back">Back to dashboard</Link>
      </header>

      <main className="pricing-main">
        <div className="pricing-intro">
          <h1>Simple pricing, 0% platform fees</h1>
          <p>
            You keep every cent of every sale. Payment goes straight to your own
            PayPal. You only pay if you want to sell beyond the free limit.
          </p>
        </div>

        {loading ? (
          <div className="pricing-loading">Loading your plan...</div>
        ) : (
          status?.known && (
            <div className={`pricing-current ${status.isLimitReached ? 'is-capped' : ''}`}>
              <div className="pricing-current-row">
                <div>
                  <strong>
                    {status.isPro ? 'You are on a paid plan' : 'Your free plan'}
                  </strong>
                  <span>
                    {status.isPro
                      ? ' Unlimited revenue, checkout is never paused.'
                      : ` $${status.totalRevenue.toFixed(2)} of ${money(
                          FREE_REVENUE_LIMIT
                        )} revenue used.`}
                  </span>
                </div>
                {!status.isPro && (
                  <button className="pricing-current-cta" onClick={() => choosePlan('pro')}>
                    Upgrade
                  </button>
                )}
              </div>
              {!status.isPro && status.limit !== null && (
                <div className="pricing-meter">
                  <div
                    className={`pricing-meter-fill ${status.isLimitReached ? 'full' : ''}`}
                    style={{ width: `${status.percentUsed}%` }}
                  />
                </div>
              )}
              {status.isLimitReached && (
                <p className="pricing-current-warning">
                  You have reached the free plan limit. Checkout is hidden on your
                  sales pages until you upgrade.
                </p>
              )}
            </div>
          )
        )}

        <div className="pricing-grid">
          {PLANS.map((plan) => {
            const isCurrent = currentPlan === plan.id;
            return (
              <div
                key={plan.id}
                className={`pricing-card ${plan.highlight ? 'highlight' : ''} ${
                  isCurrent ? 'current' : ''
                }`}
              >
                {plan.highlight && <div className="pricing-card-flag">Most popular</div>}
                <h2 className="pricing-card-name">{plan.name}</h2>
                <p className="pricing-card-tagline">{plan.tagline}</p>

                <div className="pricing-card-price">
                  {plan.priceUsd !== null ? (
                    <>
                      <span className="pricing-amount">${plan.priceUsd}</span>
                      <span className="pricing-period"> {plan.billingLabel}</span>
                    </>
                  ) : (
                    <span className="pricing-amount">Free</span>
                  )}
                </div>

                <button
                  className={`pricing-card-cta ${plan.highlight ? 'primary' : ''}`}
                  onClick={() => choosePlan(plan.id)}
                  disabled={isCurrent}
                >
                  {isCurrent ? 'Current plan' : plan.cta}
                </button>

                <ul className="pricing-features">
                  {plan.features.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
        <section className="pricing-faq">
          <h2>Questions about the free limit</h2>
          <div className="pricing-faq-grid">
            <div>
              <h3>What counts toward the ${FREE_REVENUE_LIMIT}?</h3>
              <p>
                The total revenue your products have generated on PokeShani, summed
                across every product you have, including subscription renewals.
              </p>
            </div>
            <div>
              <h3>What happens when I hit the limit?</h3>
              <p>
                The checkout button is hidden on your sales pages so buyers cannot
                check out. Your pages stay live, and upgrading brings checkout back
                immediately.
              </p>
            </div>
            <div>
              <h3>Does the limit cost me sales I already made?</h3>
              <p>
                No. You keep 100% of every sale, including anything earned before
                upgrading. PokeShani never holds or takes your money.
              </p>
            </div>
            <div>
              <h3>How do I upgrade?</h3>
              <p>
                Pick a plan, send the USDC amount on Polygon to the address
                shown, then paste your transaction hash. We verify it on-chain
                and activate your plan instantly.
              </p>
            </div>
          </div>
        </section>
      </main>

      <UpgradeModal
        open={!!upgradeFor}
        planId={upgradeFor || 'pro'}
        onClose={() => setUpgradeFor(null)}
      />
    </div>
  );
};

export default PricingPage;